import { getEnv } from "common";
import ActiveMembershipContext from "./ActiveMembershipContext";
import { useEffect, useState } from "react";
import useCurrentUser from "core/hooks/useCurrentUser";
import { ROLES } from "core/constants";
import {
  useMatch,
  useNavigate as useRRNavigate,
  useSearchParams,
} from "react-router";
import { buildMembershipSearch } from "./activeMembershipSearch";
import Loader from "core/components/Loader";
import Typography from "core/components/Typography";
import Flex from "core/components/Flex";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import styled from "styled-components";
import AnimateProviderState from "core/components/AnimateProviderState";
import { flushSync } from "react-dom";

const currentEnvironment = getEnv();
const LOCAL_KEY = "lastActiveMembership";

const getLocalActiveMembership = (userId) => {
  const value = localStorage.getItem(
    `populi:${currentEnvironment}:${userId}:${LOCAL_KEY}`
  );

  if (!value) {
    return null;
  }

  return JSON.parse(value);
};

const setLocalActiveMembership = (userId, organization) =>
  localStorage.setItem(
    `populi:${currentEnvironment}:${userId}:${LOCAL_KEY}`,
    JSON.stringify(organization)
  );

const OrganizationMembershipCard = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 5px;
  border: 1px solid #531295;
  color: #531295;
  background: #fff;
  cursor: pointer;
  scroll-snap-align: start;
  width: 100%;

  &:hover {
    background: #ddcdef;
  }
`;

export const ScrollableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  width: 421px; // 405px + padding for scrollbar
  flex-grow: 1;
  transition: scrollbar-color 150ms ease-in-out;
  scrollbar-color: hsl(265 66% 20% / 0.25) #f1eafb;
  scrollbar-width: thin;
  overflow-y: hidden;
  scrollbar-gutter: stable;
  padding-right: 16px;
  scroll-snap-type: y mandatory;
  scroll-padding: 4px;

  &:hover {
    overflow-y: auto;
    scrollbar-color: hsl(265 66% 20% / 0.6) #f1eafb;
  }
`;

const ActiveMembershipProvider = ({ children }) => {
  const navigate = useRRNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useCurrentUser();

  const membershipIdSearchParam = searchParams.get("membershipId");
  const isOnOrgRoute = useMatch("/organization/:orgId/*");
  const isOnConfigRoute = useMatch("/configurations/:orgId/*");

  const [isSwitching, setSwitching] = useState(false);
  const [activeMembership, setActiveMembership] = useState(() => {
    const localValue = getLocalActiveMembership(currentUser.userId);

    if (currentUser.memberships.length === 0) {
      return null;
    }

    if (membershipIdSearchParam) {
      const deepLinkedMembership = currentUser.memberships.find(
        (m) => m.organization_id === membershipIdSearchParam
      );

      if (deepLinkedMembership) {
        const deepLinkedValue = {
          ...deepLinkedMembership,
          id: deepLinkedMembership.organization_id,
          name: deepLinkedMembership.organization_name,
          role: ROLES[deepLinkedMembership.role],
        };
        setLocalActiveMembership(currentUser.userId, deepLinkedValue);

        return deepLinkedValue;
      }
    }

    if (!localValue) {
      if (currentUser.memberships.length === 1) {
        const singleMembership = currentUser.memberships[0];
        const onlyMembershipValue = {
          ...singleMembership,
          id: singleMembership.organization_id,
          name: singleMembership.organization_name,
          role: ROLES[singleMembership.role],
        };
        setLocalActiveMembership(currentUser.userId, onlyMembershipValue);
        return onlyMembershipValue;
      }

      return null;
    }

    return localValue;
  });

  /**
   * Handles updating activeMembership in context and localStorage
   * @param {{ id: string, name: string }} organization - organization selected
   */
  const handleActiveMembershipChange = (membership) => {
    // Force this to update before making any changes
    // Hides any layout shift that occurs from switching states
    flushSync(() => {
      setSwitching(true);
    });

    const nextMembership = {
      ...membership,
      id: membership.organization_id,
      name: membership.organization_name,
      role: ROLES[membership.role],
    };

    setLocalActiveMembership(currentUser.userId, nextMembership);
    setActiveMembership(nextMembership);

    const membershipParam = `?membershipId=${nextMembership.id}`;

    if (isOnOrgRoute) {
      navigate(`/organization/${nextMembership.id}/general${membershipParam}`);
    } else if (isOnConfigRoute) {
      navigate(`/configurations/${nextMembership.id}${membershipParam}`);
    } else {
      setSearchParams({ membershipId: nextMembership.id });
    }

    setTimeout(() => {
      setSwitching(false);
    }, 1000);
  };

  // Stamps the resolved membership onto a URL that arrived without one, merged
  // into the existing params rather than substituted for them: this fires on
  // the first render of any deep link (a shared dashboard link above all), and
  // replacing the query string would strip the state the link carries.
  useEffect(() => {
    if (membershipIdSearchParam || !activeMembership) return;

    const next = buildMembershipSearch(
      window.location.search,
      activeMembership.id
    );

    if (next) setSearchParams(next, { replace: true });
  }, [membershipIdSearchParam, activeMembership, setSearchParams]);

  return (
    <ActiveMembershipContext.Provider
      value={{
        switchActiveMembership: handleActiveMembershipChange,
        activeMembership,
      }}
    >
      <AnimateProviderState
        states={[
          {
            key: "select-membership",
            shouldRender: !activeMembership,
            child: (
              <Flex
                direction="row"
                justifyContent="center"
                alignItems="flex-start"
                height="100dvh"
                style={{ paddingTop: "20dvh" }}
              >
                <Flex direction="column" width="405px" maxHeight="80dvh">
                  <Typography
                    variant="h3"
                    style={{ minHeight: "fit-content", marginBottom: "24px" }}
                  >
                    Select an Organization to continue
                  </Typography>

                  <ScrollableList>
                    {currentUser.memberships.map((membership) => {
                      return (
                        <OrganizationMembershipCard
                          key={membership.organization_id}
                          onClick={() => {
                            handleActiveMembershipChange(membership);
                          }}
                        >
                          <span>{membership.organization_name}</span>
                          <FontAwesomeIcon icon={faAngleRight} />
                        </OrganizationMembershipCard>
                      );
                    })}
                    {currentUser.memberships.length === 0 ? (
                      <Typography
                        variant="body"
                        style={{
                          minHeight: "fit-content",
                          marginBottom: "24px",
                        }}
                      >
                        You are not currently a member of any organization.
                        Please contact support for assistance.
                      </Typography>
                    ) : null}
                  </ScrollableList>
                </Flex>
              </Flex>
            ),
          },
          {
            key: "main",
            shouldRender: activeMembership,
            child: isSwitching ? (
              <div style={{ height: "100dvh" }}>
                <Loader style={{ paddingBottom: "20vh" }} />,
              </div>
            ) : (
              children
            ),
          },
        ]}
      />
    </ActiveMembershipContext.Provider>
  );
};

export default ActiveMembershipProvider;
